import React, { useEffect, useState } from "react";
import { Box, Button, Link, Skeleton } from "@mui/material";
import { useStripe } from "@stripe/react-stripe-js";
import strings from "global/constants/StringConstants";
import { formatMoney } from "helpers/perSeatHelpers";
import {
  betterValuePack,
  fillCopy,
  formatPackBadge,
  localTierTotal,
} from "helpers/smsCreditHelpers";
import {
  buySmsCredits,
  fetchSmsCreditPacks,
  fetchSmsCreditQuote,
} from "Services/CommonServices";
import {
  SmsCreditAddOn,
  SmsCreditPurchaseResult,
  SmsCreditQuote,
} from "types/smsCredit";
import { apiStatus, getApiStatus } from "utils/apiStatus";
import {
  buyPanelHeadingSx,
  buyPanelSx,
  cheaperPackLinkSx,
  cheaperPackSx,
  customFieldSx,
  customHintSx,
  customInputSx,
  customLabelSx,
  customRowSx,
  customSuffixSx,
  errorTextSx,
  orderBarSx,
  orderButtonSx,
  orderLineSx,
  orderNoteSx,
  orderTotalSx,
  packCreditsSx,
  packGridSx,
  packLabelSx,
  packPriceSx,
  packSx,
  packTagSx,
  packUnitSx,
  paymentLinkSx,
} from "./SmsCredits.styles";

const copy = strings.smsCredits;

/**
 * Stripe's idempotency key for one purchase attempt.
 *
 * `crypto.randomUUID` is unavailable on http origins in some browsers, so this
 * falls back to a random-hex v4 shape — the value only has to be unique per
 * purchase, never cryptographically meaningful.
 */
const mintRequestId = (): string => {
  const globalCrypto = window.crypto as Crypto | undefined;
  if (globalCrypto?.randomUUID) return globalCrypto.randomUUID();
  const hex = (length: number) =>
    Array.from({ length }, () =>
      Math.floor(Math.random() * 16).toString(16),
    ).join("");
  return `${hex(8)}-${hex(4)}-4${hex(3)}-a${hex(3)}-${hex(12)}`;
};

interface Props {
  /** The panel is mounted only while open, so each open is a fresh purchase. */
  open: boolean;
  onClose: () => void;
  /** The catalogue as the card last read it; re-read here after a `409`. */
  addOn: SmsCreditAddOn;
  /** Last four of the default card, for the order bar's reassurance line. */
  cardLast4?: string;
  /**
   * A completed purchase. `balance` is the new one on an immediate charge, and
   * the unchanged one when 3-D Secure approval left the credits in flight —
   * `pending` says which, so the card can word the confirmation honestly.
   */
  onPurchased: (result: {
    quantity: number;
    balance: number;
    pending: boolean;
    invoiceUrl?: string;
  }) => void;
  /** Take the customer to the saved cards after a payment failure. */
  onManagePaymentMethods: () => void;
  /**
   * Credits stopped being on sale (the other `409`). A configuration problem,
   * not the customer's — the card re-reads the offer and drops the Buy control
   * rather than leaving a panel that can't succeed.
   */
  onOffSale: (message: string) => void;
}

/**
 * Buy prepaid SMS credits, as an inline panel inside the SMS Credits card.
 *
 * Deliberately **not** an `AppModal`: the design expands this in place under
 * the stats rather than covering the page, and the header button toggles it.
 * §11.1 governs overlays, and this isn't one.
 *
 * Everything priced here comes from the backend. There is **no single rate** —
 * each pack carries its own tier price, a typed amount takes the rate of the
 * largest pack it reaches, and `/quote` is the authority before Buy enables.
 * Nothing about pricing, limits or the pack list is hardcoded.
 *
 * Three things this has to get right, all from the contract: `expectedTotal`
 * is the quote's figure, so a price that moved mid-purchase is refused rather
 * than silently charged; one `requestId` covers every retry of a purchase so a
 * double-click cannot charge twice; and `REQUIRES_ACTION` is a first-class
 * path, not an error — Indian and European cards demand bank approval
 * routinely, and skipping it means those customers cannot buy at all.
 */
const BuySmsCreditsPanel = (props: Props) => {
  const { open, onClose, cardLast4, onPurchased } = props;
  const stripe = useStripe();

  /**
   * The catalogue actually shown. Seeded from the card's read and replaced
   * wholesale when a `409` says prices moved, so the panel always prices from
   * what the backend last told us rather than a stale render.
   */
  const [offer, setOffer] = useState<SmsCreditAddOn>(props.addOn);
  const packs = offer.packs ?? [];

  // The design pre-selects the shelf's flagged pack; otherwise the first.
  const defaultQuantity =
    packs.find((pack) => pack.badge)?.credits ??
    packs[0]?.credits ??
    offer.minQuantity;

  const [selectedPack, setSelectedPack] = useState<number>(defaultQuantity);
  const [custom, setCustom] = useState<string>("");
  const [quote, setQuote] = useState<SmsCreditQuote | null>(null);
  const [quoting, setQuoting] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [formError, setFormError] = useState<string>("");
  /** The failure was a payment one, so offer the saved-cards route with it. */
  const [needsCard, setNeedsCard] = useState<boolean>(false);
  // One idempotency key per purchase, reused across retries of that purchase.
  const [requestId, setRequestId] = useState<string>("");

  useEffect(() => {
    setOffer(props.addOn);
  }, [props.addOn]);

  // A fresh open is a fresh purchase: new idempotency key, nothing carried
  // over from a previous attempt.
  useEffect(() => {
    if (!open) return;
    setSelectedPack(defaultQuantity);
    setCustom("");
    setQuote(null);
    setFormError("");
    setNeedsCard(false);
    setRequestId(mintRequestId());
    // Only on open — re-running when `defaultQuantity` changes would throw
    // away a selection the customer had already made.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const customQuantity = parseInt(custom.replace(/[^\d]/g, ""), 10);
  const usingCustom = Number.isFinite(customQuantity) && customQuantity > 0;
  const quantity = usingCustom ? customQuantity : selectedPack;
  const isValid =
    Number.isInteger(quantity) &&
    quantity >= offer.minQuantity &&
    quantity <= offer.maxQuantity;

  /**
   * Confirm the total with the server before Buy is enabled, so the figure
   * shown and the figure billed are computed by the same tier lookup. The
   * local mirror below only fills the gap while this is in flight.
   */
  useEffect(() => {
    if (!open || !isValid) {
      setQuote(null);
      // An in-flight quote's `finally` is skipped once its effect is cleaned
      // up, so clear the flag here too — otherwise typing a quantity below the
      // minimum while one is in flight leaves the total on its skeleton for
      // good, with Buy disabled behind it.
      setQuoting(false);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        setQuoting(true);
        const response = await fetchSmsCreditQuote(quantity);
        if (!cancelled) setQuote(response ?? null);
      } catch {
        // A failed quote only costs the confirmed total — Buy stays disabled
        // rather than charging against a number the server never agreed to.
        if (!cancelled) setQuote(null);
      } finally {
        if (!cancelled) setQuoting(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, isValid, quantity]);

  const handlePickPack = (credits: number) => {
    setSelectedPack(credits);
    // Picking a pack clears a custom amount, as the design's own state does.
    setCustom("");
    setFormError("");
  };

  const handleCustomChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Digits only: a decimal or a minus would only ever be rejected.
    setCustom(event.target.value.replace(/[^\d]/g, ""));
    setFormError("");
  };

  /**
   * Prices moved between the screen reading them and the purchase pricing
   * them. Re-read the catalogue and let the quote effect above re-price the
   * same quantity, then show the backend's own message so the customer
   * confirms the new figure. Never retried silently.
   *
   * The `requestId` is deliberately **kept**: nothing was charged, so reusing
   * it is the fail-safe direction — a fresh one could charge twice if the
   * first attempt had in fact reached Stripe.
   */
  const handlePriceChanged = async (message: string) => {
    try {
      const fresh = await fetchSmsCreditPacks();
      if (fresh?.packs?.length) setOffer(fresh);
    } catch {
      // Keep the catalogue we have; the quote refresh below still corrects
      // the total, which is the figure that actually gets sent.
    }
    setQuote(null);
    setFormError(`${message} ${copy.priceChanged}`);
  };

  const handleBuy = async () => {
    // `expectedTotal` must be the quote's figure, so a missing quote is not a
    // purchase we are allowed to attempt.
    if (!isValid || !quote) return;
    try {
      setSubmitting(true);
      setFormError("");
      const result: SmsCreditPurchaseResult = await buySmsCredits({
        quantity,
        expectedTotal: quote.total,
        requestId,
      });

      if (result.status === "PAID") {
        onPurchased({
          quantity: result.quantity,
          balance: result.balance,
          pending: false,
          invoiceUrl: result.invoiceUrl,
        });
        return;
      }

      // REQUIRES_ACTION — the bank wants the cardholder to approve. Nothing is
      // charged yet and the invoice already exists, so this is never re-POSTed.
      if (!stripe || !result.clientSecret) {
        setFormError(copy.approvalUnavailable);
        return;
      }
      const { error } = await stripe.handleNextAction({
        clientSecret: result.clientSecret,
      });
      if (error) {
        // Approval failed or was abandoned: no credits, no charge. The same
        // `requestId` is kept so retrying returns this invoice, not a new one.
        setFormError(error.message ?? copy.approvalIncomplete);
        return;
      }

      // Approved. The credits are granted by Stripe's notification to the
      // backend, which is near-instant but not synchronous with this call — so
      // the balance is polled rather than incremented optimistically.
      onPurchased({
        quantity: result.quantity,
        balance: result.balance,
        pending: true,
        invoiceUrl: result.invoiceUrl,
      });
    } catch (error: any) {
      // Every message from this endpoint is written to be shown as-is, so it
      // is never parsed.
      const text =
        typeof error?.message === "string" ? error.message : copy.genericError;
      const status = getApiStatus(error);

      if (status === apiStatus.seatReductionRequired) {
        // 409 carries two unrelated meanings here — "the price changed" and
        // "not on sale right now" — and the body has no field separating
        // them. Re-reading the catalogue does: an offer that still has packs
        // is a price move the customer can confirm, an empty one means the
        // shelf is gone. That beats matching the message text, which is copy.
        try {
          const fresh = await fetchSmsCreditPacks();
          if (!fresh?.packs?.length) {
            props.onOffSale(text);
            onClose();
            return;
          }
          setOffer(fresh);
        } catch {
          // Can't tell them apart — treat it as a price change, which leaves
          // the customer able to retry rather than hiding the control.
        }
        await handlePriceChanged(text);
        return;
      }

      // A 402 covers both "no saved card" and "card declined" and the body
      // carries nothing that separates them, so the panel shows the reason and
      // offers the card route alongside it rather than guessing which it was.
      // (Backend Request: a machine-readable `reason`.)
      setFormError(text);
      setNeedsCard(status === apiStatus.paymentRequired);
    } finally {
      setSubmitting(false);
    }
  };

  // Instant feedback while the server's quote is in flight, mirroring the
  // backend's tier rule; the confirmed total always replaces it before Buy is
  // enabled, and is what actually gets sent as `expectedTotal`.
  const total = quote?.total ?? (isValid ? localTierTotal(quantity, packs) : 0);
  const currency = quote?.currency ?? offer.currency;
  const canBuy = isValid && !quoting && Boolean(quote) && !submitting;

  /** The rate this quantity earned — the quote's, or the tier it reaches. */
  const earnedRate =
    quote?.unitPrice ??
    (isValid && quantity > 0 ? localTierTotal(quantity, packs) / quantity : 0);

  /**
   * Volume tiers mean a typed amount just under a pack can cost more than the
   * pack. Only ever shown for a typed amount — a pack is by definition already
   * the best price for its own quantity.
   */
  const betterPack = usingCustom
    ? betterValuePack(quantity, total, packs)
    : undefined;

  const orderLine = isValid
    ? fillCopy(usingCustom ? copy.orderLineCustom : copy.orderLinePack, {
        quantity: quantity.toLocaleString(),
        unit: formatMoney(earnedRate, currency),
      })
    : fillCopy(copy.orderLineInvalid, {
        min: offer.minQuantity.toLocaleString(),
        max: offer.maxQuantity.toLocaleString(),
      });

  return (
    <Box sx={buyPanelSx}>
      <Box sx={buyPanelHeadingSx}>{copy.choosePack}</Box>

      <Box sx={packGridSx}>
        {packs.map((pack) => {
          const selected = !usingCustom && selectedPack === pack.credits;
          return (
            <Box
              key={pack.planId}
              component="button"
              type="button"
              onClick={() => handlePickPack(pack.credits)}
              aria-pressed={selected}
              sx={packSx(selected)}
            >
              {pack.badge && (
                <Box component="span" sx={packTagSx}>
                  {formatPackBadge(pack.badge)}
                </Box>
              )}
              <Box component="span" sx={packCreditsSx}>
                {pack.credits.toLocaleString()}
              </Box>
              <Box component="span" sx={packLabelSx}>
                {copy.packCreditsLabel}
              </Box>
              <Box component="span" sx={packPriceSx}>
                {formatMoney(pack.price, offer.currency)}
              </Box>
              <Box component="span" sx={packUnitSx}>
                {fillCopy(copy.packUnit, {
                  unit: formatMoney(pack.unitPrice, offer.currency),
                })}
              </Box>
            </Box>
          );
        })}
      </Box>

      <Box sx={customRowSx}>
        <Box component="span" sx={customLabelSx}>
          {copy.orEnterAmount}
        </Box>
        <Box sx={customFieldSx}>
          <Box
            component="input"
            type="text"
            inputMode="numeric"
            value={custom}
            onChange={handleCustomChange}
            placeholder={copy.customPlaceholder}
            aria-label={copy.customAriaLabel}
            disabled={submitting}
            sx={customInputSx}
          />
          <Box component="span" sx={customSuffixSx}>
            {copy.creditsWord}
          </Box>
        </Box>
        <Box component="span" sx={customHintSx}>
          {fillCopy(copy.customHint, {
            min: offer.minQuantity.toLocaleString(),
          })}
        </Box>
      </Box>

      {/* More credits for less money is never what they meant to do. */}
      {betterPack && (
        <Box sx={cheaperPackSx} role="status">
          {fillCopy(copy.cheaperPack, {
            credits: betterPack.credits.toLocaleString(),
            amount: formatMoney(betterPack.price, offer.currency),
          })}{" "}
          <Box
            component="button"
            type="button"
            onClick={() => handlePickPack(betterPack.credits)}
            sx={cheaperPackLinkSx}
          >
            {fillCopy(copy.cheaperPackAction, {
              credits: betterPack.credits.toLocaleString(),
            })}
          </Box>
        </Box>
      )}

      <Box sx={orderBarSx}>
        <Box component="span" sx={orderLineSx}>
          {orderLine}
        </Box>
        <Box component="span" sx={orderNoteSx}>
          {cardLast4
            ? fillCopy(copy.orderNoteCard, { last4: cardLast4 })
            : copy.orderNote}
        </Box>
        {quoting ? (
          <Skeleton variant="text" width={80} sx={orderTotalSx} />
        ) : (
          <Box component="span" sx={orderTotalSx}>
            {isValid ? formatMoney(total, currency) : copy.noTotal}
          </Box>
        )}
        <Button
          variant="contained"
          disableRipple
          onClick={handleBuy}
          disabled={!canBuy}
          sx={orderButtonSx(canBuy)}
        >
          {submitting ? copy.buying : copy.buyConfirm}
        </Button>
      </Box>

      {formError && (
        <Box sx={errorTextSx} role="alert">
          {formError}
          {needsCard && (
            <>
              {" "}
              <Link
                component="button"
                type="button"
                onClick={props.onManagePaymentMethods}
                sx={paymentLinkSx}
              >
                {copy.managePaymentMethods}
              </Link>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default BuySmsCreditsPanel;
